import { produce } from 'immer';

import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { LineSegment2D, Point2D, point2DEquals } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { InsertingTrussEnvelopeData, IPickingObjectInfo, SnappingLevel } from '../../../store/slices/applicationSlice';
import { EntitySnapTargetSource } from '../../layout-snapping/EntitySnapTargetSource';
import { PointSnappingSettings } from '../../layout-snapping/point-snapping/settings';
import { snapMousePosition } from '../../layout-snapping/point-snapping/snapMousePosition';
import { GeometricEcosystem } from '../ecosystem/GeometricEcosystem';
import { RoofContainerBasedGeometricEcosystem } from '../ecosystem/RoofContainerBasedGeometricEcosystem';
import { EnvelopeCalculationResult } from './calculation/envelopeCalculationResult';
import { StageOneTrussEnvelope } from './calculation/stage1';
import { StageTwoTrussEnvelope } from './calculation/stage2';
import { TrussEnvelopeInsertionSettings } from './types';

export type FullyDefinedInsertingTrussEnvelopeData = InsertingTrussEnvelopeData &
  Required<
    Pick<
      InsertingTrussEnvelopeData,
      'leftPoint' | 'rightPoint' | 'roofContainerGuid' | 'elevation' | 'leftOverhang' | 'rightOverhang'
    >
  >;

/**
 * This class contains everything a React component or redux action might need to know about the active insertion
 *   envelope. Use the `getTrussEnvelopeInsertionInformation` selector to obtain an instance.
 */
export class TrussEnvelopeInsertionInformation {
  readonly activeMode: 'parallel-line' | 'point-selection';

  readonly settings: TrussEnvelopeInsertionSettings;
  readonly snappingSettings: PointSnappingSettings;
  readonly snappingLevel: SnappingLevel;

  readonly data: InsertingTrussEnvelopeData;

  readonly ecosystem: GeometricEcosystem;

  constructor(props: {
    settings: TrussEnvelopeInsertionSettings;
    snappingSettings: PointSnappingSettings;
    snappingLevel: SnappingLevel;
    ecosystem: GeometricEcosystem;
    insertionData: InsertingTrussEnvelopeData;
    pickingInfo: IPickingObjectInfo;
  }) {
    this.settings = props.settings;
    this.snappingSettings = props.snappingSettings;
    this.snappingLevel = props.snappingLevel;
    this.ecosystem = props.ecosystem;

    this.activeMode =
      props.pickingInfo?.type === 'pickingLayoutPoint' && props.pickingInfo.target.startsWith('insertion-envelope')
        ? 'point-selection'
        : 'parallel-line';

    this.data = produce(props.insertionData, (draft) => {
      // If we're currently re-picking a point, we don't want the preview envelope to use point that's being replaced.
      if (props.pickingInfo?.type === 'pickingLayoutPoint') {
        switch (props.pickingInfo.target) {
          case 'insertion-envelope-left':
            delete draft.leftPoint;
            break;

          case 'insertion-envelope-right':
            delete draft.rightPoint;
            break;
        }
      }
    });
  }

  hasRoofContainer(): this is { data: { roofContainerGuid: string } } {
    return this.data.roofContainerGuid != null;
  }

  isInsertionEnvelopeFullyDefined(): this is { data: FullyDefinedInsertingTrussEnvelopeData } {
    return this.isArbitraryTrussEnvelopeDataFullyDefined(this.data);
  }

  isFeaturePatternContainerInferenceActive(): boolean {
    // Only applicable to feature pattern envelopes, obviously.
    if (this.data.definitionType !== 'FeaturePattern') return false;

    // Don't break a fully defined envelope by changing or clearing its container guid.
    if (this.isInsertionEnvelopeFullyDefined()) return false;

    // In fact, if either point has been chosen we shouldn't be messing with the container guid as long as there is one.
    if ((this.data.leftPoint || this.data.rightPoint) && this.data.featurePatternContainerGuid) return false;

    return true;
  }

  isArbitraryTrussEnvelopeDataFullyDefined(
    data: InsertingTrussEnvelopeData,
  ): data is FullyDefinedInsertingTrussEnvelopeData {
    if (this.ecosystem instanceof RoofContainerBasedGeometricEcosystem && data.roofContainerGuid == null) return false;
    if (data.definitionType === 'FeaturePattern' && data.featurePatternContainerGuid == null) return false;

    if (
      data.leftPoint == null ||
      data.rightPoint == null ||
      data.elevation == null ||
      data.leftOverhang == null ||
      data.rightOverhang == null
    )
      return false;
    if (point2DEquals(data.leftPoint, data.rightPoint)) return false;

    return true;
  }

  snapMousePositionForPointSelection(mousePosition: Point2D) {
    const otherPointSource = this.data.leftPoint ?? this.data.rightPoint ?? null;
    const otherPoint = otherPointSource ? Point2D.fromObject(otherPointSource) : null;

    const targetSource = new EntitySnapTargetSource(this.ecosystem);
    return snapMousePosition(mousePosition, this.snappingSettings, targetSource, {
      otherPoint,
      distanceSnappingLevel: this.snappingLevel,
    }).point;
  }

  // both the insertion truss visuals and automatic run dimensions consume the same `TrussEnvelopeInsertionInformation`,
  //   so this cache eliminates a duplicated envelope calculation each frame
  private _cachedInsertionEnvelope: {
    mousePosition: Point2D;
    result: EnvelopeCalculationResult | null;
  } | null = null;

  calculateInsertionEnvelopeUsingMousePositionIfPossible(mousePosition: Point2D): EnvelopeCalculationResult | null {
    if (
      this._cachedInsertionEnvelope &&
      this._cachedInsertionEnvelope.mousePosition.x === mousePosition.x &&
      this._cachedInsertionEnvelope.mousePosition.y === mousePosition.y
    ) {
      return this._cachedInsertionEnvelope.result;
    }

    const result = this.calculateInsertionEnvelopeUsingMousePositionIfPossibleUncached(mousePosition);
    this._cachedInsertionEnvelope = { mousePosition, result };

    return result;
  }

  private calculateInsertionEnvelopeUsingMousePositionIfPossibleUncached(
    mousePosition: Point2D,
  ): EnvelopeCalculationResult | null {
    let snappedMousePosition = mousePosition;

    if (this.activeMode === 'point-selection') {
      snappedMousePosition = this.snapMousePositionForPointSelection(mousePosition);
    }

    // In point-selection mode we skip the calculation pipeline and use the picked points as-is.
    if (this.activeMode === 'point-selection') {
      const leftPoint = this.data.leftPoint ? Point2D.fromObject(this.data.leftPoint) : snappedMousePosition;
      const rightPoint = this.data.rightPoint ? Point2D.fromObject(this.data.rightPoint) : snappedMousePosition;
      if (leftPoint.equals(rightPoint)) return null;

      const { leftOverhang = 0, rightOverhang = 0 } = this.data;

      const elevation = StageTwoTrussEnvelope.calculateElevation(
        new LineSegment2D(leftPoint, rightPoint),
        leftOverhang,
        rightOverhang,
        this.ecosystem,
        this.data,
      );

      return EnvelopeCalculationResult.fromDistinctPoints(
        leftPoint,
        rightPoint,
        elevation,
        leftOverhang,
        rightOverhang,
      );
    }

    return (
      this.createStageOneTrussEnvelope(snappedMousePosition)
        ?.boundIntoSegmentAroundDefiningPoint(this.data)
        ?.adjustToAccountForBevels(this.settings.bevelCutTypePreference)
        ?.getResult() ?? null
    );
  }

  private createStageOneTrussEnvelope(mousePosition: Point2D) {
    switch (this.activeMode) {
      case 'parallel-line':
        return StageOneTrussEnvelope.fromSnappedParallelLine(
          mousePosition,
          this.settings.snapping,
          this.snappingSettings,
          this.snappingLevel,
          this.ecosystem,
        );

      case 'point-selection': {
        const [pointA, pointB] = this.combinePreviouslySelectedPointsWithMousePosition(mousePosition);

        if (pointA && pointB) {
          return StageOneTrussEnvelope.fromPoints(pointA, pointB, {
            ecosystem: this.ecosystem,
            origin: { type: 'point-selection' },
          });
        }

        return null;
      }

      default:
        assertExhaustiveSwitch(this.activeMode);
    }
  }

  // Point selection mode requires two points, so see if the mouse position can be combined with a previously selected
  //   point to get what we need.
  private combinePreviouslySelectedPointsWithMousePosition(
    mousePosition: Point2DInterface,
  ): [Point2DInterface, Point2DInterface] | [null, null] {
    if (this.data.leftPoint && this.data.rightPoint) return [this.data.leftPoint, this.data.rightPoint];
    if (this.data.leftPoint) return [this.data.leftPoint, mousePosition];
    if (this.data.rightPoint) return [mousePosition, this.data.rightPoint];

    return [null, null];
  }
}
